import { parseSearchParam } from "@/src/common/utils"
import { useAnalytics } from "@/src/sb/components/Analytics"
import AngleDown from "@/src/sb/components/icon/AngleDown"
import Envelope from "@/src/sb/components/icon/Envelope"
import MobileNavArrow from "@/src/sb/components/icon/MobileNavArrow"
import SignOut from "@/src/sb/components/icon/SignOut"
import SplitPanel from "@/src/sb/components/icon/SplitPanel"
import User from "@/src/sb/components/icon/User"
import { V2_FEATURE_TOGGLES } from "@/src/sb/constants/configs"
import {
  MenuItemOption,
  accounts,
  authOptions
} from "@/src/sb/constants/copy/navItems"
import {
  B2C_SUNSET,
  ENABLE_EMAIL_PREFERENCES
} from "@/src/sb/constants/featureFlags"
import useRequestDemo from "@/src/sb/hooks/useRequestDemo"
import { useUser } from "@/src/sb/hooks/useUser"
import { useFeatureIsOn } from "@growthbook/growthbook-react"
import classNames from "classnames"
import Link from "next/link"
import { useRouter } from "next/router"
import { useEffect, useMemo, useRef, useState } from "react"

type SidebarUserAccountProps = {
  userName: string
  userType: keyof typeof accounts
  onOptionClick?: (label: string) => void
  setNextQueryParam?: boolean
  onClose?: () => void
  defaultOpen?: boolean
}

type SidebarAccountLink = Extract<MenuItemOption, { href: string }>

const menuAlreadyExists = (
  menuItem: MenuItemOption,
  menuItems: MenuItemOption[]
) =>
  menuItems.some((item) => {
    if ("type" in item && item.type === "separator") return false
    if ("type" in menuItem && menuItem.type === "separator") return false
    const linkItem = item as SidebarAccountLink
    const linkMenuItem = menuItem as SidebarAccountLink
    return linkItem.label === linkMenuItem.label
  })

const SidebarUserAccount = ({
  userName,
  userType,
  onOptionClick,
  setNextQueryParam,
  onClose,
  defaultOpen = false
}: SidebarUserAccountProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { asPath = "" } = useRouter()
  const analytics = useAnalytics()
  const user = useUser()
  const requestDemo = useRequestDemo()
  const emailPreferencesEnabled = useFeatureIsOn(ENABLE_EMAIL_PREFERENCES)

  const HAS_PRO_ROLE = user?.data?.roles?.includes("pro")

  const requestDemoMenuItem = {
    src: "/assets/icons/navItems/upgrade.svg",
    href: requestDemo.url,
    label: "Request Demo",
    canHaveNextQueryParam: true,
    handleClick: () =>
      requestDemo.trackClickCorporateQuote({ context: "sidebar" })
  }

  const menuItems: MenuItemOption[] = useMemo(() => {
    let items: MenuItemOption[] = accounts[userType]
      ? [...accounts[userType]]
      : []

    // Filter out "Switch to Stears Pro" if user doesn't have pro role
    if (!HAS_PRO_ROLE) {
      items = items.filter((item) => {
        if ("type" in item && item.type === "separator") return true
        const linkItem = item as SidebarAccountLink
        return linkItem.label !== "Switch to Stears Pro"
      })
    }

    // Filter out Admin if user is not an admin
    if (!user?.data?.isAdmin) {
      items = items.filter((item) => {
        if ("type" in item && item.type === "separator") return true
        const linkItem = item as SidebarAccountLink
        return linkItem.label !== "Admin"
      })
    }

    // Filter out Email Preferences while the feature is behind its flag
    if (!emailPreferencesEnabled) {
      items = items.filter((item) => {
        if ("type" in item && item.type === "separator") return true
        const linkItem = item as SidebarAccountLink
        return linkItem.label !== authOptions.emailPreferences.label
      })
    }

    // Add upgrade/demo option for logged in users
    if (userType === "loggedIn" && !requestDemo.loading) {
      let menuItemToAdd = authOptions.upgrade
      if (V2_FEATURE_TOGGLES.has(B2C_SUNSET)) {
        menuItemToAdd = requestDemoMenuItem
      }
      if (!menuAlreadyExists(menuItemToAdd, items)) {
        const insertIndex = items.length - 1
        items = [
          ...items.slice(0, insertIndex),
          menuItemToAdd,
          ...items.slice(insertIndex)
        ]
      }
    }

    // Clean up consecutive separators
    items = items.filter((item, index, arr) => {
      if ("type" in item && item.type === "separator") {
        const prevItem = arr[index - 1]
        const nextItem = arr[index + 1]
        // Remove if first, last, or next to another separator
        if (
          index === 0 ||
          index === arr.length - 1 ||
          (prevItem && "type" in prevItem && prevItem.type === "separator") ||
          !nextItem
        ) {
          return false
        }
      }
      return true
    })

    return items
  }, [
    userType,
    requestDemo.loading,
    HAS_PRO_ROLE,
    user?.data?.isAdmin,
    emailPreferencesEnabled
  ])

  const displayMenuItems: MenuItemOption[] = useMemo(() => {
    const linkItems = menuItems.filter(
      (item): item is SidebarAccountLink =>
        !("type" in item && item.type === "separator")
    )

    const normalizedItems: SidebarAccountLink[] = linkItems.map((item) => {
      if (item.label === "My Profile") {
        return {
          ...item,
          icon: (
            <span className="flex items-center justify-center rounded-full text-[#D1D5DB] border border-white">
              <User size={16} />
            </span>
          ),
          label: "Profile"
        }
      }

      if (item.label === "Switch to Stears Pro") {
        return {
          ...item,
          icon: <SplitPanel size={16} />
        }
      }

      if (item.label === "Admin") {
        return {
          ...item,
          icon: <MobileNavArrow size={16} />
        }
      }

      if (item.label === "Email Preferences") {
        return {
          ...item,
          icon: <Envelope size={16} />
        }
      }

      if (item.label === "Sign out") {
        return {
          ...item,
          icon: <SignOut size={16} />,
          label: "Sign Out"
        }
      }

      return item
    })

    return normalizedItems.flatMap((item, index): MenuItemOption[] => {
      const previousItem = normalizedItems[index - 1]
      const shouldSeparateFromPro =
        previousItem?.label === "Switch to Stears Pro"
      const shouldSeparateSignOut = item.label === "Sign Out"

      return shouldSeparateFromPro || shouldSeparateSignOut
        ? [{ type: "separator" }, item]
        : [item]
    })
  }, [menuItems])

  useEffect(() => {
    setIsOpen(defaultOpen)
  }, [defaultOpen])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen])

  const getHref = (item: SidebarAccountLink) => {
    if (
      setNextQueryParam &&
      "canHaveNextQueryParam" in item &&
      item.canHaveNextQueryParam
    ) {
      return `${item.href}?${parseSearchParam({
        next: asPath
      }).toString()}`
    }
    return item.href
  }

  const handleOptionClick = (item: SidebarAccountLink) => {
    setIsOpen(false)
    onOptionClick?.(item.label)
    onClose?.()
    if ("handleClick" in item && item.handleClick) {
      item.handleClick(analytics, { context: "sidebar" })
    }
  }

  return (
    <div ref={dropdownRef} className="relative z-30 h-[60px] shrink-0">
      <button
        data-testid="sidebar-account-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label={`${userName} account menu`}
        className={classNames(
          "h-[60px] w-full flex items-center gap-2.5 px-5 text-sm",
          "bg-[#0E0F10] text-[#F9FAFB] hover:bg-[#0E0F10] active:bg-[#0E0F10] focus:bg-[#0E0F10]",
          "border-b-[0.5px] dark:border-gray-600 focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-[#2A2F35]"
        )}
      >
        <span className="flex h-6 w-6 items-center justify-center rounded-full text-[#D1D5DB] p-[4px] bg-[#2A2F35]">
          <User size={24} />
        </span>
        <span className="max-w-[150px] truncate text-left font-jakarta text-sm font-medium leading-[22px] text-white">
          {userName}
        </span>
        <span
          className={classNames(
            "text-[#9CA3AF] transition-transform",
            isOpen ? "rotate-180" : ""
          )}
        >
          <AngleDown size={14} />
        </span>
      </button>

      {isOpen && (
        <div
          data-testid="sidebar-account-menu"
          role="menu"
          className={classNames(
            "absolute top-full left-5 right-5 z-[60]",
            "overflow-hidden rounded-[3px] border border-[#2A2F35] py-2",
            "bg-[#15181C] text-[#F9FAFB] shadow-2xl"
          )}
        >
          {displayMenuItems.map((item, index) => {
            if ("type" in item && item.type === "separator") {
              return (
                <hr
                  key={`separator-${index}`}
                  className="my-1 border-[#2A2F35]"
                />
              )
            }

            const linkItem = item as SidebarAccountLink

            return (
              <Link
                key={linkItem.label}
                href={getHref(linkItem)}
                onClick={() => handleOptionClick(linkItem)}
                role="menuitem"
                className={classNames(
                  "flex items-center gap-[10px] px-[10px] py-2",
                  "!text-white visited:!text-white hover:!text-white",
                  "!no-underline visited:!no-underline hover:!no-underline focus:!no-underline active:!no-underline",
                  "text-[12px] font-medium leading-4",
                  "hover:bg-[#20242A]",
                  "transition-colors [&_*]:!text-white [&_svg:not([data-stroke-icon])_path]:!fill-current"
                )}
              >
                {linkItem.icon && (
                  <span className="flex h-[16px] w-[16px] shrink-0 items-center justify-center !text-white">
                    {typeof linkItem.icon === "string" ? (
                      <img
                        src={linkItem.icon}
                        alt={linkItem.label}
                        className="h-[16px] w-[16px] brightness-0 invert"
                      />
                    ) : (
                      linkItem.icon
                    )}
                  </span>
                )}
                <span className="!text-white">{linkItem.label}</span>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default SidebarUserAccount
